import { useEffect, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { FilterMatchMode } from 'primereact/api';
import { getVacantes } from './Integracion';
import VacanteDialog from './NuevaVacanteDialog';
import CambiarEstatusDialog from './CambiarEstatusDialog';
import { exportarVacantesExcel } from './exportarVacantesExcel';
import useGetDataUser from '../../../utilidades/customHooks/useGetDataUser'; // ajusta la ruta


const FILTROS_INICIALES = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    tipo: { value: null, matchMode: FilterMatchMode.EQUALS },
    puesto: { value: null, matchMode: FilterMatchMode.EQUALS },
    cliente: { value: null, matchMode: FilterMatchMode.EQUALS },
    zona: { value: null, matchMode: FilterMatchMode.EQUALS },
    id: { value: null, matchMode: FilterMatchMode.EQUALS },
};

const FILTROS_DROPDOWN = [
    { field: 'tipo', label: 'Tipo' },
    { field: 'puesto', label: 'Puesto' },
    { field: 'cliente', label: 'Cliente' },
    { field: 'zona', label: 'Zona' },
];

const SEVERIDAD_ESTATUS = {
    ABIERTA: 'info',
    'EN PROCESO': 'warning',
    CUBIERTA: 'success',
    CANCELADA: 'danger',
};

const formatFecha = (fecha) => new Date(fecha).toLocaleDateString('es-MX');

// fechaConclusion llega como YYYY-MM-DD; se formatea sin Date para evitar desfase de zona horaria
const formatFechaConclusion = (fecha) => (fecha ? fecha.split('-').reverse().join('/') : '');

// Días naturales desde la creación de la vacante hasta hoy
const calcularDiasTranscurridos = (fecha) => {
    const inicio = new Date(fecha);
    inicio.setHours(0, 0, 0, 0);
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    return Math.round((hoy - inicio) / 86400000);
};

const ESTATUS_ABIERTA = 1;
const ESTATUS_FINALES = [3, 4];

// Coinciden con TH_VACANTE_ESTATUS
const OPCIONES_ESTATUS = [
    { label: 'Abierta', value: 1 },
    { label: 'En proceso', value: 2 },
    { label: 'Cubierta', value: 3 },
    { label: 'Cancelada', value: 4 },
];

// Convierte un Date a "YYYY-MM-DD" con componentes locales (evita el desfase de zona horaria)
const aFechaISO = (fecha) => {
    if (!fecha) return undefined;
    const y = fecha.getFullYear();
    const m = String(fecha.getMonth() + 1).padStart(2, '0');
    const d = String(fecha.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

// Rango por defecto del filtro de fecha: últimos 30 días hasta hoy
const obtenerRangoUltimos30Dias = () => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const hace30Dias = new Date(hoy);
    hace30Dias.setDate(hoy.getDate() - 29);
    return { inicio: hace30Dias, fin: hoy };
};
// Usuarios con permiso para cambiar el estatus de las vacantes (IDs separados por coma en el .env)
const USUARIOS_CAMBIO_ESTATUS = (process.env.REACT_APP_USUARIOS_CAMBIO_ESTATUS_VACANTES ?? '')
    .split(',')
    .map((id) => Number(id.trim()))
    .filter(Boolean);

const Vacantes = () => {
    const userData = useGetDataUser();
    const idUsuario = userData?.user?.ID;
    const puedeCambiarEstatus = USUARIOS_CAMBIO_ESTATUS.includes(Number(idUsuario));
    const [vacantes, setVacantes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [filtros, setFiltros] = useState(FILTROS_INICIALES);
    const [dialog, setDialog] = useState(null); // 'vacante' | 'estatus' | null
    const [vacanteSeleccionada, setVacanteSeleccionada] = useState(null);
    const [filtradas, setFiltradas] = useState(null); // Lo que muestra la tabla con los filtros aplicados
    const [filtroEstatus, setFiltroEstatus] = useState([]);
    const [fechaInicio, setFechaInicio] = useState(() => obtenerRangoUltimos30Dias().inicio);
    const [fechaFin, setFechaFin] = useState(() => obtenerRangoUltimos30Dias().fin);
    const toast = useRef(null);

    // El filtro de fecha solo se muestra/aplica cuando se eligen vacantes Cubiertas o Canceladas
    const mostrarFiltroFecha = filtroEstatus.some((e) => ESTATUS_FINALES.includes(e));

    // El backend filtra estatus y fecha para no traer todo el historial: sin estatus elegido
    // no regresa Cubiertas ni Canceladas, y el rango de fechas solo acota a esas dos.
    const cargarVacantes = () => {
        setLoading(true);
        getVacantes({
            idUsuario,
            ...(filtroEstatus.length ? { estatus: filtroEstatus.join(',') } : {}),
            ...(mostrarFiltroFecha ? { start: aFechaISO(fechaInicio), end: aFechaISO(fechaFin) } : {}),
        })
            .then((data) =>
                setVacantes(data.map((v) => ({ ...v, diasTranscurridos: calcularDiasTranscurridos(v.fecha) })))
            )
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (idUsuario) cargarVacantes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idUsuario, filtroEstatus, fechaInicio, fechaFin]);

    const abrirDialog = (tipo, vacante = null) => {
        setVacanteSeleccionada(vacante);
        setDialog(tipo);
    };

    const onGuardado = (mensaje) => {
        setDialog(null);
        toast.current?.show({ severity: 'success', summary: mensaje, life: 3000 });
        cargarVacantes();
    };

    const setFiltro = (field, value) =>
        setFiltros({ ...filtros, [field]: { ...filtros[field], value } });

    // Opciones de cada filtro a partir de los datos ya cargados
    const opciones = (field) =>
        [...new Set(vacantes.map((v) => v[field]).filter(Boolean))].sort();

    // El rango por defecto (últimos 30 días) no cuenta como filtro activo
    const rangoUltimos30Dias = obtenerRangoUltimos30Dias();
    const fechaEsPorDefecto =
        aFechaISO(fechaInicio) === aFechaISO(rangoUltimos30Dias.inicio) &&
        aFechaISO(fechaFin) === aFechaISO(rangoUltimos30Dias.fin);

    const hayFiltros =
        Object.values(filtros).some((f) => f.value) ||
        filtroEstatus.length > 0 ||
        (mostrarFiltroFecha && !fechaEsPorDefecto);

    const limpiarFiltros = () => {
        setFiltros(FILTROS_INICIALES);
        setFiltroEstatus([]);
        setFechaInicio(rangoUltimos30Dias.inicio);
        setFechaFin(rangoUltimos30Dias.fin);
    };

    const exportarExcel = () => {
        const filtrosActivos = FILTROS_DROPDOWN
            .filter(({ field }) => filtros[field].value)
            .map(({ field, label }) => `${label}: ${filtros[field].value}`);
        if (filtroEstatus.length) {
            const nombres = OPCIONES_ESTATUS.filter((o) => filtroEstatus.includes(o.value)).map((o) => o.label);
            filtrosActivos.unshift(`Estatus: ${nombres.join(' / ')}`);
        } else {
            filtrosActivos.unshift('Estatus: Abierta / En proceso');
        }
        if (mostrarFiltroFecha && (fechaInicio || fechaFin)) {
            const desde = fechaInicio ? fechaInicio.toLocaleDateString('es-MX') : '...';
            const hasta = fechaFin ? fechaFin.toLocaleDateString('es-MX') : '...';
            filtrosActivos.push(`Conclusión: ${desde} - ${hasta}`);
        }
        if (filtros.global.value) filtrosActivos.unshift(`Búsqueda: "${filtros.global.value}"`);

        exportarVacantesExcel(filtradas ?? vacantes, filtrosActivos);
    };

    const header = (
        <div className="flex flex-column gap-2">
            <div className="flex flex-column sm:flex-row gap-2">
                <InputText
                    value={filtros.global.value ?? ''}
                    onChange={(e) => setFiltro('global', e.target.value)}
                    placeholder="Buscar por ID, puesto, cliente, solicitante..."
                    aria-label="Buscar vacantes"
                    className="w-full"
                />
                <Button
                    label="Limpiar filtros"
                    icon="pi pi-filter-slash"
                    outlined
                    disabled={!hayFiltros}
                    onClick={limpiarFiltros}
                />
            </div>
            <div className="grid">
                <div className="col-12 sm:col-6 lg:col">
                    <MultiSelect
                        value={filtroEstatus}
                        options={OPCIONES_ESTATUS}
                        onChange={(e) => setFiltroEstatus(e.value)}
                        placeholder="Estatus"
                        aria-label="Estatus"
                        display="chip"
                        className="w-full"
                    />
                </div>
                {/* Fecha: solo aplica al ver vacantes Cubiertas/Canceladas */}
                {mostrarFiltroFecha && (
                    <>
                        <div className="col-12 sm:col-6 lg:col">
                            <Calendar
                                value={fechaInicio}
                                onChange={(e) => setFechaInicio(e.value ?? null)}
                                placeholder="Conclusión desde"
                                aria-label="Conclusión desde"
                                dateFormat="dd/mm/yy"
                                maxDate={fechaFin ?? undefined}
                                showIcon
                                showButtonBar
                                className="w-full"
                            />
                        </div>
                        <div className="col-12 sm:col-6 lg:col">
                            <Calendar
                                value={fechaFin}
                                onChange={(e) => setFechaFin(e.value ?? null)}
                                placeholder="Conclusión hasta"
                                aria-label="Conclusión hasta"
                                dateFormat="dd/mm/yy"
                                minDate={fechaInicio ?? undefined}
                                showIcon
                                showButtonBar
                                className="w-full"
                            />
                        </div>
                    </>
                )}
                {FILTROS_DROPDOWN.map(({ field, label }) => (
                    <div key={field} className="col-12 sm:col-6 lg:col">
                        <Dropdown
                            value={filtros[field].value}
                            options={opciones(field)}
                            onChange={(e) => setFiltro(field, e.value)}
                            placeholder={label}
                            aria-label={label}
                            showClear
                            filter
                            className="w-full"
                        />
                    </div>
                ))}
            </div>
        </div>
    );

    const puestoBody = (row) => (
        <span className="flex align-items-center gap-2">
            {row.puesto}
            {row.confidencial && (
                <i className="pi pi-lock text-500" title="Confidencial" aria-label="Confidencial" />
            )}
        </span>
    );

    const accionesBody = (row) => (
        <div className="flex gap-1">
            {row.idUsuarioSolicitante === Number(idUsuario) && (
                <Button
                    icon="pi pi-pencil"
                    rounded
                    text
                    aria-label="Editar vacante"
                    tooltip={row.idEstatus === ESTATUS_ABIERTA ? 'Editar' : 'Solo se editan vacantes abiertas'}
                    tooltipOptions={{ position: 'left', showOnDisabled: true }}
                    disabled={row.idEstatus !== ESTATUS_ABIERTA}
                    onClick={() => abrirDialog('vacante', row)}
                />
            )}
            {puedeCambiarEstatus && (
                <Button
                    icon="pi pi-sync"
                    rounded
                    text
                    aria-label="Cambiar estatus"
                    tooltip={ESTATUS_FINALES.includes(row.idEstatus) ? 'Vacante concluida' : 'Cambiar estatus'}
                    tooltipOptions={{ position: 'left', showOnDisabled: true }}
                    disabled={ESTATUS_FINALES.includes(row.idEstatus)}
                    onClick={() => abrirDialog('estatus', row)}
                />
            )}
        </div>
    );

    const estatusBody = (row) => (
        <Tag value={row.estatus} severity={SEVERIDAD_ESTATUS[row.estatus]} />
    );

    if (error) {
        return (
            <Message
                severity="error"
                text="No se pudieron cargar las vacantes. Recarga la página para intentar de nuevo."
                className="w-full"
            />
        );
    }

    return (
        <div className="p-3">
            <Toast ref={toast} />
            <div className="flex align-items-center justify-content-between gap-2 mb-3">
                <h2 className="m-0">Vacantes</h2>
                <div className="flex gap-2">
                    <Button
                        label="Exportar Excel"
                        icon="pi pi-file-excel"
                        outlined
                        disabled={loading || !(filtradas ?? vacantes).length}
                        onClick={exportarExcel}
                    />
                    <Button label="Nueva vacante" icon="pi pi-plus" onClick={() => abrirDialog('vacante')} />
                </div>
            </div>
            <DataTable
                value={vacantes}
                loading={loading}
                header={header}
                filters={filtros}
                onValueChange={setFiltradas}
                globalFilterFields={['id', 'puesto', 'cliente', 'solicitante', 'reemplaza', 'zona', 'descripcion']}
                sortField="id"
                sortOrder={1}
                scrollable
                scrollHeight="calc(100vh - 260px)"
                size="small"
                stripedRows
                tableStyle={{ minWidth: '75rem' }}
                emptyMessage="No hay vacantes con estos filtros."
            >
                <Column field="id" header="ID" sortable />
                <Column field="fecha" header="Fecha" body={(row) => formatFecha(row.fecha)} sortable />
                <Column field="diasTranscurridos" header="Días transcurridos" sortable />
                <Column field="puesto" header="Puesto" body={puestoBody} sortable />
                <Column field="tipo" header="Tipo" sortable />
                <Column field="descripcion" header="Descripcion" sortable />
                <Column field="cliente" header="Cliente" sortable />
                <Column field="zona" header="Zona" sortable />
                <Column field="solicitante" header="Solicitante" sortable />
                <Column field="reemplaza" header="Reemplaza a" />
                <Column field="cubre" header="Cubierta por" />
                <Column field="estatus" header="Estatus" body={estatusBody} sortable />
                <Column
                    field="fechaConclusion"
                    header="Conclusión"
                    body={(row) => formatFechaConclusion(row.fechaConclusion)}
                    sortable
                />
                <Column header="Acciones" body={accionesBody} style={{ width: '6rem' }} />
            </DataTable>

            <VacanteDialog
                visible={dialog === 'vacante'}
                vacante={vacanteSeleccionada}
                idUsuario={idUsuario}
                onHide={() => setDialog(null)}
                onSaved={onGuardado}
            />

            <CambiarEstatusDialog
                visible={puedeCambiarEstatus && dialog === 'estatus'}
                vacante={vacanteSeleccionada}
                onHide={() => setDialog(null)}
                onSaved={onGuardado}
            />
        </div>
    );
};

export default Vacantes;